import {
	type MediaTrackKind,
	type MediaTrackState,
	mediaTrackStateKey,
	type SegmentMediaBundle,
} from "./media-track-types";

const DEFAULT_MAX_POOLED_ELEMENTS = 6;
const SEGMENT_TRACK_READY_TIMEOUT_MS = 15_000;

interface PooledMediaElement {
	segmentId: string;
	kind: MediaTrackKind;
	element: HTMLMediaElement;
	src: string;
	lastUsedAt: number;
}

export interface MediaSyncCoordinatorOptions {
	getSegmentIds?: () => readonly string[];
	onTrackStatesChange?: (states: Record<string, MediaTrackState>) => void;
	createAudioElement?: () => HTMLAudioElement;
	createVideoElement?: () => HTMLVideoElement;
	maxPooledElements?: number;
	trackReadyTimeoutMs?: number;
}

export interface MediaSyncCoordinator {
	preloadSegment: (bundle: SegmentMediaBundle) => void;
	syncPlaybackPosition: (
		segmentId: string | null,
		positionMs: number,
	) => void;
	waitForSegmentTracksReady: (
		segmentId: string,
		kinds: readonly MediaTrackKind[],
	) => Promise<boolean>;
	areTracksReady: (
		segmentId: string,
		kinds: readonly MediaTrackKind[],
	) => boolean;
	getTrackStates: () => Record<string, MediaTrackState>;
	resolveAudioElement: (segmentId: string) => HTMLAudioElement | null;
	resolveVideoElement: (segmentId: string) => HTMLVideoElement | null;
	destroy: () => void;
}

function createDefaultAudioElement(): HTMLAudioElement {
	if (typeof document === "undefined") {
		throw new Error("MediaSyncCoordinator requires a document environment");
	}
	const audio = document.createElement("audio");
	audio.preload = "auto";
	return audio;
}

function createDefaultVideoElement(): HTMLVideoElement {
	if (typeof document === "undefined") {
		throw new Error("MediaSyncCoordinator requires a document environment");
	}
	const video = document.createElement("video");
	video.preload = "auto";
	video.playsInline = true;
	video.muted = true;
	return video;
}

const MEDIA_HAVE_FUTURE_DATA = 3;

function isMediaElementReady(element: HTMLMediaElement): boolean {
	return element.readyState >= MEDIA_HAVE_FUTURE_DATA;
}

function resolveBufferedPercent(element: HTMLMediaElement): number {
	const duration = element.duration;
	if (!Number.isFinite(duration) || duration <= 0) {
		return 0;
	}
	if (element.buffered.length === 0) {
		return 0;
	}
	const end = element.buffered.end(element.buffered.length - 1);
	return Math.max(0, Math.min(100, Math.round((end / duration) * 100)));
}

function elementHasSource(element: HTMLMediaElement, src: string): boolean {
	const current = element.currentSrc || element.src || "";
	if (!current) {
		return false;
	}
	try {
		const resolved = new URL(src, window.location.href).href;
		return current === src || current === resolved;
	} catch {
		return current.includes(src);
	}
}

function createInitialTrackState(
	segmentId: string,
	kind: MediaTrackKind,
): MediaTrackState {
	return {
		segmentId,
		kind,
		src: null,
		preloadStatus: "idle",
		bufferedPercent: 0,
		isReadyForPlayback: false,
		durationMs: null,
	};
}

export function createMediaSyncCoordinator(
	options: MediaSyncCoordinatorOptions = {},
): MediaSyncCoordinator {
	const createAudioElement =
		options.createAudioElement ?? createDefaultAudioElement;
	const createVideoElement =
		options.createVideoElement ?? createDefaultVideoElement;
	const maxPooledElements =
		options.maxPooledElements ?? DEFAULT_MAX_POOLED_ELEMENTS;
	const trackReadyTimeoutMs =
		options.trackReadyTimeoutMs ?? SEGMENT_TRACK_READY_TIMEOUT_MS;

	const pool = new Map<string, PooledMediaElement>();
	const trackStates = new Map<string, MediaTrackState>();
	const readyWaiters = new Map<string, Set<(ready: boolean) => void>>();

	let destroyed = false;

	const emitStates = () => {
		options.onTrackStatesChange?.(
			Object.fromEntries(trackStates.entries()),
		);
	};

	const setTrackState = (state: MediaTrackState) => {
		trackStates.set(mediaTrackStateKey(state.segmentId, state.kind), state);
		emitStates();
	};

	const notifyReadyWaiters = (segmentId: string, kind: MediaTrackKind) => {
		const key = mediaTrackStateKey(segmentId, kind);
		const waiters = readyWaiters.get(key);
		if (!waiters) {
			return;
		}
		for (const resolve of waiters) {
			resolve(true);
		}
		waiters.clear();
	};

	const updateTrackFromElement = (
		segmentId: string,
		kind: MediaTrackKind,
		element: HTMLMediaElement,
		src: string,
		durationMs: number,
	) => {
		const canPlay = isMediaElementReady(element);
		const measuredDurationMs =
			Number.isFinite(element.duration) && element.duration > 0
				? Math.round(element.duration * 1000)
				: durationMs;

		setTrackState({
			segmentId,
			kind,
			src,
			preloadStatus: canPlay ? "ready" : "loading",
			bufferedPercent: resolveBufferedPercent(element),
			isReadyForPlayback: canPlay,
			durationMs: measuredDurationMs,
		});

		if (canPlay) {
			notifyReadyWaiters(segmentId, kind);
		}
	};

	const attachElementListeners = (
		segmentId: string,
		kind: MediaTrackKind,
		element: HTMLMediaElement,
		src: string,
		durationMs: number,
	) => {
		const refresh = () => {
			if (destroyed) {
				return;
			}
			updateTrackFromElement(segmentId, kind, element, src, durationMs);
		};

		element.addEventListener("loadedmetadata", refresh);
		element.addEventListener("canplay", refresh);
		element.addEventListener("progress", refresh);
		element.addEventListener("error", () => {
			setTrackState({
				segmentId,
				kind,
				src,
				preloadStatus: "error",
				bufferedPercent: 0,
				isReadyForPlayback: false,
				durationMs,
			});
			notifyReadyWaiters(segmentId, kind);
		});

		refresh();
	};

	const evictOldestIfNeeded = () => {
		if (pool.size < maxPooledElements) {
			return;
		}
		let oldestKey: string | null = null;
		let oldestUsedAt = Number.POSITIVE_INFINITY;
		for (const [key, entry] of pool.entries()) {
			if (entry.lastUsedAt < oldestUsedAt) {
				oldestUsedAt = entry.lastUsedAt;
				oldestKey = key;
			}
		}
		if (oldestKey != null) {
			const entry = pool.get(oldestKey);
			entry?.element.pause();
			entry?.element.removeAttribute("src");
			entry?.element.load();
			pool.delete(oldestKey);
			trackStates.delete(oldestKey);
		}
	};

	const resolveOrCreateElement = (
		segmentId: string,
		kind: MediaTrackKind,
		src: string,
		durationMs: number,
	): HTMLMediaElement => {
		const key = mediaTrackStateKey(segmentId, kind);
		const existing = pool.get(key);
		if (existing != null && elementHasSource(existing.element, src)) {
			existing.lastUsedAt = Date.now();
			return existing.element;
		}

		evictOldestIfNeeded();

		const element =
			kind === "audio" ? createAudioElement() : createVideoElement();

		if (existing != null) {
			existing.element.pause();
		}

		pool.set(key, {
			segmentId,
			kind,
			element,
			src,
			lastUsedAt: Date.now(),
		});

		setTrackState({
			...createInitialTrackState(segmentId, kind),
			src,
			preloadStatus: "loading",
			durationMs,
		});

		attachElementListeners(segmentId, kind, element, src, durationMs);
		element.src = src;
		element.load();
		return element;
	};

	const preloadDescriptor = (
		segmentId: string,
		kind: MediaTrackKind,
		descriptor: {
			src: string;
			durationMs: number;
			posterUrl?: string | null;
		},
	) => {
		const element = resolveOrCreateElement(
			segmentId,
			kind,
			descriptor.src,
			descriptor.durationMs,
		);
		if (kind === "video" && descriptor.posterUrl) {
			(element as HTMLVideoElement).poster = descriptor.posterUrl;
		}
	};

	const areTracksReadyInternal = (
		segmentId: string,
		kinds: readonly MediaTrackKind[],
	) =>
		kinds.every((kind) => {
			const state = trackStates.get(mediaTrackStateKey(segmentId, kind));
			return state?.isReadyForPlayback === true;
		});

	return {
		preloadSegment(bundle) {
			if (destroyed) {
				return;
			}
			if (bundle.audio) {
				preloadDescriptor(bundle.segmentId, "audio", bundle.audio);
			}
			if (bundle.video) {
				preloadDescriptor(bundle.segmentId, "video", bundle.video);
			}
		},
		syncPlaybackPosition(segmentId, positionMs) {
			if (destroyed) {
				return;
			}
			const clampedSeconds = Math.max(0, positionMs) / 1000;

			for (const entry of pool.values()) {
				if (segmentId != null && entry.segmentId !== segmentId) {
					entry.element.pause();
					continue;
				}
				if (segmentId == null) {
					entry.element.pause();
					continue;
				}
				if (
					Number.isFinite(entry.element.duration) &&
					entry.element.duration > 0
				) {
					entry.element.currentTime = Math.min(
						clampedSeconds,
						entry.element.duration,
					);
				} else {
					entry.element.currentTime = clampedSeconds;
				}
				entry.lastUsedAt = Date.now();
			}
		},
		areTracksReady(segmentId, kinds) {
			return areTracksReadyInternal(segmentId, kinds);
		},
		waitForSegmentTracksReady(segmentId, kinds) {
			if (destroyed) {
				return Promise.resolve(false);
			}
			if (kinds.length === 0) {
				return Promise.resolve(true);
			}
			if (areTracksReadyInternal(segmentId, kinds)) {
				return Promise.resolve(true);
			}

			return new Promise<boolean>((resolve) => {
				const timeoutId = setTimeout(() => {
					cleanup();
					resolve(false);
				}, trackReadyTimeoutMs);

				const cleanup = () => {
					clearTimeout(timeoutId);
					for (const kind of kinds) {
						const key = mediaTrackStateKey(segmentId, kind);
						readyWaiters.get(key)?.delete(checkAllReady);
					}
				};

				const checkAllReady = () => {
					if (!areTracksReadyInternal(segmentId, kinds)) {
						return;
					}
					cleanup();
					resolve(true);
				};

				for (const kind of kinds) {
					const key = mediaTrackStateKey(segmentId, kind);
					const waiters = readyWaiters.get(key) ?? new Set();
					waiters.add(checkAllReady);
					readyWaiters.set(key, waiters);
				}
			});
		},
		getTrackStates() {
			return Object.fromEntries(trackStates.entries());
		},
		resolveAudioElement(segmentId) {
			const entry = pool.get(mediaTrackStateKey(segmentId, "audio"));
			return entry?.kind === "audio"
				? (entry.element as HTMLAudioElement)
				: null;
		},
		resolveVideoElement(segmentId) {
			const entry = pool.get(mediaTrackStateKey(segmentId, "video"));
			return entry?.kind === "video"
				? (entry.element as HTMLVideoElement)
				: null;
		},
		destroy() {
			destroyed = true;
			for (const entry of pool.values()) {
				entry.element.pause();
				entry.element.removeAttribute("src");
				entry.element.load();
			}
			pool.clear();
			trackStates.clear();
			readyWaiters.clear();
			emitStates();
		},
	};
}
